import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { API_URL } from '../config/apiConfig';
import {
  FaCheckCircle,
  FaHourglass,
  FaCircle,
  FaTimes,
  FaSpinner,
} from 'react-icons/fa';

export default function MyComplaints() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const { user: userInfo } = useSelector((state) => state.auth);

  useEffect(() => {
    if (userInfo?.token) {
      fetchSupportMessages();
    }
  }, [userInfo?.token]);

  const fetchSupportMessages = async () => {
    try {
      setLoading(true);
      const config = {
        headers: {
          Authorization: `Bearer ${userInfo?.token}`,
        },
      };

      const response = await axios.get(
        `${API_URL}/support-message/my-messages?page=1&limit=100`,
        config
      );

      if (response.data.success) {
        setMessages(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching support messages:', error);
      toast.error('Failed to load support messages');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open':
        return <FaCircle className="text-red-600" />;
      case 'in-progress':
        return <FaHourglass className="text-yellow-600" />;
      case 'resolved':
        return <FaCheckCircle className="text-green-600" />;
      case 'closed':
        return <FaTimes className="text-gray-600" />;
      default:
        return <FaCircle className="text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open':
        return 'text-red-600 bg-red-50';
      case 'in-progress':
        return 'text-yellow-600 bg-yellow-50';
      case 'resolved':
        return 'text-green-600 bg-green-50';
      case 'closed':
        return 'text-gray-600 bg-gray-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div>
      {/* HEADER */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">My Complaints & Support Messages</h2>
        {loading && <FaSpinner className="animate-spin text-blue-600" size={20} />}
      </div>

      {/* DIVIDER LINE */}
      <div className="mb-6 w-full h-0 border-t border-gray-400 opacity-60"></div>

      {/* No Messages State */}
      {!loading && messages.length === 0 && (
        <div className="text-center py-12">
          <FaCircle className="text-gray-300 text-4xl mx-auto mb-4" />
          <p className="text-gray-500 text-lg font-medium">
            No support messages submitted yet
          </p>
          <p className="text-gray-400 text-sm mt-2">
            Submit a complaint from your booking page to get support assistance
          </p>
        </div>
      )}

      {/* Messages List */}
      {!loading && messages.length > 0 && (
        <div className="space-y-4">
          {messages.map((message) => (
            <div key={message._id} className="border border-gray-200 rounded-lg overflow-hidden">
              {/* Message Header */}
              <div
                onClick={() =>
                  setExpandedId(expandedId === message._id ? null : message._id)
                }
                className="bg-gray-50 p-4 cursor-pointer hover:bg-gray-100 transition"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {getStatusIcon(message.status)}
                      <h3 className="font-semibold text-gray-900 text-lg">
                        {message.title}
                      </h3>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600 flex-wrap">
                      <span>Booking: <strong>{message.bookingNumber || message.bookingId}</strong></span>
                      <span>{formatDate(message.createdAt)}</span>
                      <span
                        className={`px-3 py-1 rounded-full font-medium text-xs ${getStatusColor(
                          message.status
                        )}`}
                      >
                        {message.status.charAt(0).toUpperCase() +
                          message.status.slice(1).replace('-', ' ')}
                      </span>
                    </div>
                  </div>
                  <div className="text-gray-400 ml-4">
                    {expandedId === message._id ? '▼' : '▶'}
                  </div>
                </div>
              </div>

              {/* Message Details - Expandable */}
              {expandedId === message._id && (
                <div className="p-4 border-t border-gray-200 bg-white">
                  {/* User Description */}
                  <div className="mb-4">
                    <p className="text-sm font-semibold text-gray-700 mb-2">
                      Your Complaint
                    </p>
                    <p className="text-gray-700 whitespace-pre-wrap bg-gray-50 p-3 rounded text-sm">
                      {message.description}
                    </p>
                  </div>

                  {/* Admin Reply */}
                  {message.adminReply && (
                    <div className="mb-4 border-t border-gray-200 pt-4">
                      <p className="text-sm font-semibold text-gray-700 mb-2">
                        Admin Response
                      </p>
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <p className="text-xs text-gray-600 mb-2">
                          Replied by {message.repliedByAdmin?.name || 'Admin'} on{' '}
                          {formatDate(message.adminRepliedAt)}
                        </p>
                        <p className="text-gray-700 whitespace-pre-wrap text-sm">
                          {message.adminReply}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* No Reply Yet */}
                  {!message.adminReply && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-sm text-blue-800">
                        ⏳ Your complaint is being reviewed. Admin will respond soon.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
